/**
 * Studio plugins — the single source of truth (SAL-2912).
 *
 * Some features are only meaningful to a handful of studios. Rather than let
 * every one of them grow its own boolean column, its own `fetchXEnabled()`
 * server action and its own scattered `display:` checks (which is how
 * `okrsEnabled`, `badgesEnabled` and the module-row gates each grew), a plugin
 * declares once what it *owns* and the rest of the app derives everything from
 * that declaration:
 *
 *   • `routes`   — nav links under these prefixes vanish when the plugin is off,
 *                  and the routes themselves are guarded server-side, so a
 *                  bookmarked URL stops working too.
 *   • `sections` — playbook anchors (`/playbook#fbiv`) the plugin owns.
 *   • `usageKey` — how "is this studio actually using it?" is counted. Used by
 *                  the backfill and by the Plugins tab to warn before a
 *                  disable that would hide real content.
 *
 * Adding a plugin means adding an entry here. It should not mean editing
 * the side nav, the search index or the settings form.
 *
 * This file is deliberately dependency-free (plain data, icons by name) so it
 * can be imported from client components, server components, route guards,
 * unit tests and one-off scripts alike.
 */

/**
 * @typedef {object} PluginDescriptor
 * @property {string} key            Stable id. Persisted in `Workspace.plugins`; never rename.
 * @property {string} label          UI name. Safe to rename.
 * @property {string} summary        One line: what the plugin gives the studio.
 * @property {string} whenDisabled   What an admin should expect after turning it off.
 * @property {string} iconName       lucide-react export name, resolved by the Plugins tab.
 * @property {boolean} defaultEnabled Applied when the studio has no explicit choice.
 * @property {string[]} routes       Route prefixes this plugin owns.
 * @property {string[]} sections     Playbook section anchors this plugin owns.
 * @property {string} usageKey       Key into lib/plugins/usage.js counters.
 * @property {string} usageNoun      Plural noun for the usage count ("objectives").
 * @property {string} usageSingularNoun Singular of `usageNoun`. Spelled out
 *   rather than derived: stripping a trailing "s" turns "entries" into
 *   "entrie" and "communities" into "communitie".
 */

/** @type {PluginDescriptor[]} */
export const PLUGINS = [
  {
    key: "okrs",
    label: "OKRs",
    summary:
      "Assign objectives and key results, then track progress against them.",
    whenDisabled:
      "Objectives stay saved and reminder emails stop. OKRs disappear from the admin menu and from everyone's results.",
    iconName: "Target",
    defaultEnabled: false,
    routes: ["/workspaces/admin/okrs", "/dashboard/okrs"],
    sections: [],
    usageKey: "okrs",
    usageNoun: "objectives",
    usageSingularNoun: "objective",
  },
  {
    key: "capability-maps",
    label: "Capability Maps",
    summary:
      "Map features to benefits, impact and value themes, and test reps on them.",
    whenDisabled:
      "Entries and capability map tests stay saved. The library, its tests and the playbook section are hidden.",
    iconName: "Layers",
    defaultEnabled: false,
    routes: ["/workspaces/admin/fbiv", "/workspaces/admin/fbiv-tests"],
    sections: ["fbiv"],
    usageKey: "capabilityMaps",
    usageNoun: "entries",
    usageSingularNoun: "entry",
  },
  {
    key: "takeaway-reports",
    label: "Takeaway Reports",
    summary:
      "Turn simulations and conversations into reusable written takeaways.",
    whenDisabled:
      "Reports stay saved. The library is hidden and takeaways stop appearing in the playbook.",
    iconName: "NotebookPen",
    defaultEnabled: false,
    // Only the library. /workspaces/admin/data/takeaway-reports/[id] is
    // deliberately absent: despite the name it is the generic Example editor
    // and also opens pitches, outbound scripts, discovery trees and objection
    // handling, so claiming it here would break editing those.
    routes: ["/workspaces/admin/takeaway-reports"],
    sections: ["independent-takeaways"],
    usageKey: "takeawayReports",
    usageNoun: "reports",
    usageSingularNoun: "report",
  },
  {
    key: "communities",
    label: "Communities",
    summary:
      "Group members into communities with their own leaders and reporting.",
    whenDisabled:
      "Communities stay saved, and anyone who already leads one keeps their reporting access. Only the management screens are hidden.",
    iconName: "Users",
    defaultEnabled: false,
    routes: ["/workspaces/admin/people/communities"],
    sections: [],
    usageKey: "communities",
    usageNoun: "communities",
    usageSingularNoun: "community",
  },
  {
    key: "feedback-comments",
    label: "Feedback Comments",
    summary:
      "Let members annotate their AI feedback and transcripts, and discuss them with the Salesably team.",
    whenDisabled:
      "Existing comment threads stay saved. The Comments button disappears from results and the Feedback Comments pages are hidden.",
    iconName: "MessageSquareText",
    defaultEnabled: false,
    // /feedback-comments is the thread page (and /feedback-comments/new the
    // draft a member annotates before anything is stored). The superUser
    // console at /admin/superUser/meta-feedback is deliberately absent: it
    // reads across every studio and must keep working whatever a studio picks.
    routes: [
      "/feedback-comments",
      "/dashboard/feedback-comments",
      "/workspaces/reports/feedback-comments",
    ],
    sections: [],
    usageKey: "feedbackComments",
    usageNoun: "comment threads",
    usageSingularNoun: "comment thread",
  },
  {
    key: "voice-tone",
    label: "Voice Tone",
    summary:
      "Analyse the vocal tone on saved practice recordings and imported calls and show it as a timeline under the recording.",
    whenDisabled:
      "Tone already scored stays saved. New recordings are not analysed and the timeline is hidden from results.",
    iconName: "AudioLines",
    defaultEnabled: false,
    // No page of its own: the timeline sits on the result pages, which read
    // the switch from the result's studio (lib/emotion/score-result.js scores
    // only when it is on).
    routes: [],
    sections: [],
    usageKey: "voiceTone",
    usageNoun: "scored recordings",
    usageSingularNoun: "scored recording",
  },
];

/** Plugin keys in registry order. */
export const PLUGIN_KEYS = PLUGINS.map((plugin) => plugin.key);

const BY_KEY = new Map(PLUGINS.map((plugin) => [plugin.key, plugin]));

/** @returns {PluginDescriptor | null} */
export function getPlugin(key) {
  return BY_KEY.get(key) || null;
}

/**
 * Every route prefix owned by any plugin, paired with its key. Sorted longest
 * first so the most specific owner wins: `/workspaces/admin/fbiv-tests` must
 * not be claimed by a shorter prefix that happens to share its opening
 * segments.
 */
const OWNED_ROUTES = PLUGINS.flatMap((plugin) =>
  plugin.routes.map((route) => ({ route, key: plugin.key })),
).sort((a, b) => b.route.length - a.route.length);

const OWNED_SECTIONS = new Map(
  PLUGINS.flatMap((plugin) =>
    plugin.sections.map((section) => [section, plugin.key]),
  ),
);

/**
 * `sections` are anchors on this one page. The fragment lookup is scoped to it
 * so an unrelated link that happens to share an anchor name — `/dashboard#fbiv`
 * — isn't hidden along with the plugin.
 */
const SECTION_PATH = "/playbook";

/**
 * Strip the locale prefix, query string and trailing slash so an href can be
 * compared against `routes`. Mirrors the normalization the nav search index
 * does in lib/navigation/page-directory.js.
 */
function normalizePath(href) {
  if (typeof href !== "string" || !href) return "";
  const [withoutQuery] = href.split("?", 1);
  return withoutQuery
    .replace(/^\/(es|fr-CA)(?=\/|$)/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

/**
 * Which plugin owns this href, if any.
 *
 * Matches on path segments, not string prefixes, so `/dashboard/okrs` never
 * claims a future `/dashboard/okrs-archive`. A `#fragment` on `/playbook` is
 * matched against `sections` first, which is what lets that one page be partly
 * owned: `/playbook#fbiv` belongs to Capability Maps while `/playbook` itself
 * belongs to nobody. The same fragment elsewhere (`/dashboard#fbiv`) is not a
 * playbook anchor and stays unowned.
 *
 * @returns {string | null} plugin key
 */
export function getPluginOwningHref(href) {
  const path = normalizePath(href);
  if (!path) return null;

  const [pathname, fragment] = path.split("#", 2);
  if (pathname === SECTION_PATH && fragment) {
    const sectionOwner = OWNED_SECTIONS.get(fragment);
    if (sectionOwner) return sectionOwner;
  }

  for (const { route, key } of OWNED_ROUTES) {
    if (pathname === route || pathname.startsWith(`${route}/`)) return key;
  }
  return null;
}

/** Which plugin owns this playbook anchor, if any. */
export function getPluginOwningSection(sectionId) {
  if (typeof sectionId !== "string") return null;
  return OWNED_SECTIONS.get(sectionId.toLowerCase()) || null;
}
